import React, { useMemo, useState } from 'react'

const API_BASE = '/api/ask-stewie/analytics'

const defaultHeaders = {
  'Content-Type': 'application/json',
}

// Mock responses for all endpoints
const MOCK_RESPONSES = {
  ask: {
    topic: 'union',
    routed_source: 'union',
    short_answer: 'Based on Article 12 of the IBEW Contract, management must follow seniority rules for overtime distribution.',
    plain_english: 'The union contract requires that overtime be offered in order of seniority. If you were bypassed, you may have grounds for a grievance. Review Article 12, Section 3 for the specific language.',
    source_references: [
      { source_name: 'IBEW Contract', section_title: 'Overtime Equalization', section_number: 'Article 12 Section 3' },
      { source_name: 'IBEW Contract', section_title: 'Overtime Bypass Grievance', section_number: 'Article 12 Section 4' },
      { source_name: 'IBEW Contract', section_title: 'Seniority Rights', section_number: 'Article 8 Section 1' },
    ],
    recommended_next_step: 'Document the dates and times you were bypassed, then contact your steward to discuss filing a grievance.',
    suggest_issue_report: true,
  },
  report: {
    ok: true,
    message: 'Report submitted successfully',
    report_id: Math.floor(Math.random() * 10000) + 1000,
    anonymous: false,
  },
  'job-briefing': {
    work_type: 'switching',
    work_environment: 'Substation energized equipment',
    potential_hazards: [
      'Energized equipment nearby',
      'Arc flash potential',
      'Stored energy in capacitors',
      'Slip/trip hazards',
      'Environmental conditions (weather)',
    ],
    recommended_ppe: [
      'Hard hat (Class E)',
      'Safety glasses with side shields',
      'Arc-rated clothing (40 cal/cm²)',
      'Voltage-rated gloves',
      'Steel-toe boots',
      'Hearing protection',
    ],
    safety_reminders: [
      'Verify zero energy state before work begins',
      'Use proper lockout/tagout procedures',
      'Maintain minimum approach distances',
      'Check weather conditions before outdoor work',
      'Ensure communication devices are functional',
    ],
    source_references: [
      { source_name: 'Safety Rulebook', section_title: 'Switching Operations', section_number: 'Chapter 4.2' },
      { source_name: 'Safety Rulebook', section_title: 'PPE Requirements', section_number: 'Chapter 2.1' },
    ],
    disclaimer: 'This briefing is for reference only. Always follow your company\'s specific safety procedures and consult with your supervisor for site-specific hazards.',
  },
  adminReport: {
    ok: true,
    report_type: 'search_topics',
    start_date: '2026-03-01',
    end_date: '2026-03-07',
    filters: { topic: null, issue_type: null, location: null },
    rows: [
      { topic: 'union', count: 15 },
      { topic: 'safety', count: 8 },
      { topic: 'discipline', count: 5 },
      { topic: 'general', count: 3 },
    ],
    row_count: 4,
  },
  factFinder: {
    feature: 'fact_finder_preparation',
    incident_type: 'Operational / Switching',
    possible_topics: ['procedure review', 'equipment operation', 'safety investigation'],
    what_this_meeting_may_involve: 'This appears to be an operational / switching matter. A fact-finding meeting is typically used to gather information, understand what occurred, review procedures or policies, and document the sequence of events.',
    prep_checklist: [
      'Write down the sequence of events while it is still fresh.',
      'Review switching orders or related procedures.',
      'Note who was present and what instructions were given.',
      'Bring a calm, factual timeline of what you remember.',
      'Consider asking whether a union representative may be present, if applicable.',
    ],
    suggested_questions: [
      'Can you clarify the purpose of this fact finder?',
      'Will this meeting be informational, or could it lead to discipline?',
      'Will a union representative be present?',
      'Can I review any incident reports or notes before the meeting?',
      'What specific event or concern is being reviewed?',
    ],
    areas_to_review: ['switching procedures', 'equipment operating procedures', 'incident timeline'],
    fact_summary: 'Incident Type: Operational / Switching\nMeeting Type: fact finder\nWorker Role: involved\nTask Being Performed: breaker switching\nLocation: substation\n\nIncident Description:\nSupervisor told me there will be a fact finder tomorrow about a switching error during breaker operations.\n\nTimeline:\n1. Time: 08:15 | Action: Reviewed switching order with crew | Equipment: breaker 12A | Present: crew leader, operator | Notes: Initial briefing completed\n2. Time: 08:32 | Action: Performed switching step | Equipment: breaker 12A control switch | Present: crew leader, operator | Notes: Unexpected relay indication observed\n\nSummary Note: Focus on the facts as you remember them, the sequence of events, who was present, and what procedures or instructions were involved.',
    disclaimer: 'This tool provides general informational guidance and preparation support. It does not replace advice from your union representative, company policies, or legal counsel.',
  },
}

async function mockRequest(kind, request) {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 800))
  
  if (kind === 'ask') return MOCK_RESPONSES.ask
  if (kind === 'report') return { ...MOCK_RESPONSES.report, report_id: Math.floor(Math.random() * 10000) + 1000 }
  if (kind === 'job-briefing') return MOCK_RESPONSES['job-briefing']
  if (kind === 'adminReport') return MOCK_RESPONSES.adminReport
  if (kind === 'factFinder') return MOCK_RESPONSES.factFinder
  
  return { error: 'Unknown request type' }
}

async function apiRequestWithFallback(path, options = {}, auth = {}) {
  const headers = {
    ...defaultHeaders,
    ...(auth.userId ? { 'X-User-Id': auth.userId } : {}),
    ...(auth.isAdmin ? { 'X-Is-Admin': 'true' } : {}),
    ...(options.headers || {}),
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  const contentType = response.headers.get('content-type') || ''
  const isJson = contentType.includes('application/json')
  const payload = isJson ? await response.json() : await response.text()

  if (!response.ok) {
    const message = typeof payload === 'string' ? payload : payload?.detail || 'Request failed'
    throw new Error(message)
  }

  return payload
}

async function smartRequest(kind, request, mockApi) {
  if (mockApi) {
    return mockRequest(kind, request)
  }
  // For fact finder, use absolute path (different API prefix)
  if (request.useAbsolutePath) {
    const response = await fetch(request.path, {
      ...request.options,
      headers: {
        ...defaultHeaders,
        ...(request.auth?.userId ? { 'X-User-Id': request.auth.userId } : {}),
        ...(request.auth?.isAdmin ? { 'X-Is-Admin': 'true' } : {}),
      },
      credentials: 'include',
    })
    const contentType = response.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')
    const payload = isJson ? await response.json() : await response.text()
    if (!response.ok) {
      const message = typeof payload === 'string' ? payload : payload?.detail || 'Request failed'
      throw new Error(message)
    }
    return payload
  }
  return apiRequestWithFallback(request.path, request.options, request.auth)
}

function useAuth() {
  const [auth, setAuth] = useState({
    userId: 'user-1001',
    isAdmin: true,
  })
  const [mockApi, setMockApi] = useState(true) // Default to mock mode

  return {
    auth,
    setAuth,
    mockApi,
    setMockApi,
  }
}

function useApiState(initialData = null) {
  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function run(task) {
    setLoading(true)
    setError('')
    try {
      const result = await task()
      setData(result)
      return result
    } catch (err) {
      setError(err.message || 'Something went wrong')
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    data,
    setData,
    loading,
    error,
    setError,
    run,
  }
}

export default function AskStewieApp() {
  const { auth, setAuth, mockApi, setMockApi } = useAuth()
  const [route, setRoute] = useState('ask')

  const pages = [
    { key: 'ask', label: 'Ask Stewie' },
    { key: 'report', label: 'Report Issue' },
    { key: 'briefing', label: 'Job Briefing' },
    { key: 'factfinder', label: 'Fact Finder' },
    { key: 'admin', label: 'Admin Analytics' },
  ]

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-6 md:px-8 md:py-10 space-y-8">
        <header className="rounded-[32px] border border-slate-200 bg-white shadow-sm p-6 md:p-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2">
                <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm shadow-sm">
                  AskStewie App Shell
                </div>
                {mockApi && (
                  <div className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm shadow-sm text-amber-700 font-medium">
                    🔶 MOCK API MODE
                  </div>
                )}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">AskStewie Frontend</h1>
              <p className="max-w-3xl text-slate-600 text-base md:text-lg">
                A wired frontend starter for Ask Stewie, issue reporting, job briefing assistance,
                and admin analytics. {mockApi ? 'Currently running with mock data.' : 'Connected to live API.'}
              </p>
            </div>

            <AuthPanel auth={auth} setAuth={setAuth} mockApi={mockApi} setMockApi={setMockApi} />
          </div>
        </header>

        <nav className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {pages.map((page) => (
            <button
              key={page.key}
              onClick={() => setRoute(page.key)}
              data-testid={`nav-${page.key}`}
              className={`rounded-2xl border px-4 py-4 text-left transition ${
                route === page.key
                  ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                  : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-100'
              }`}
            >
              <div className="text-sm opacity-70">Page</div>
              <div className="font-semibold mt-1">{page.label}</div>
            </button>
          ))}
        </nav>

        {route === 'ask' && <AskPage auth={auth} mockApi={mockApi} onNavigate={setRoute} />}
        {route === 'report' && <ReportIssuePage auth={auth} mockApi={mockApi} />}
        {route === 'briefing' && <JobBriefingPage auth={auth} mockApi={mockApi} />}
        {route === 'factfinder' && <FactFinderPage auth={auth} mockApi={mockApi} />}
        {route === 'admin' && <AdminAnalyticsPage auth={auth} mockApi={mockApi} />}
      </div>
    </div>
  )
}

function AuthPanel({ auth, setAuth, mockApi, setMockApi }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 md:p-5 min-w-[300px]">
      <div className="text-sm font-semibold text-slate-700">Session Preview</div>
      <div className="mt-4 space-y-4">
        <div>
          <FieldLabel>User ID Header</FieldLabel>
          <TextInput
            value={auth.userId}
            onChange={(e) => setAuth((prev) => ({ ...prev, userId: e.target.value }))}
            placeholder="user-1001"
            data-testid="user-id-input"
          />
        </div>

        <label className="flex items-center gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={auth.isAdmin}
            onChange={(e) => setAuth((prev) => ({ ...prev, isAdmin: e.target.checked }))}
            data-testid="admin-mode-checkbox"
          />
          Admin mode enabled
        </label>

        <div className="border-t border-slate-200 pt-4">
          <label className="flex items-center gap-3 text-sm font-medium text-amber-700">
            <input
              type="checkbox"
              checked={mockApi}
              onChange={(e) => setMockApi(e.target.checked)}
              data-testid="mock-api-checkbox"
              className="accent-amber-600"
            />
            Mock API Mode
          </label>
          <p className="text-xs text-slate-500 mt-1 ml-6">
            {mockApi ? 'Using simulated responses' : 'Connected to live backend'}
          </p>
        </div>
      </div>
    </div>
  )
}

function CardShell({ title, subtitle, children, action }) {
  return (
    <section className="rounded-[32px] bg-white border border-slate-200 shadow-sm overflow-hidden">
      <div className="border-b border-slate-100 px-6 py-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
          <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
        </div>
        {action ? <div>{action}</div> : null}
      </div>
      <div className="p-6">{children}</div>
    </section>
  )
}

function FieldLabel({ children }) {
  return <label className="block text-sm font-medium text-slate-700 mb-2">{children}</label>
}

function TextInput(props) {
  return (
    <input
      {...props}
      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-300"
    />
  )
}

function TextArea(props) {
  return (
    <textarea
      {...props}
      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-300 min-h-[140px]"
    />
  )
}

function Select(props) {
  return (
    <select
      {...props}
      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-300"
    />
  )
}

function PrimaryButton({ children, ...props }) {
  return (
    <button
      {...props}
      className="rounded-2xl bg-slate-900 text-white px-5 py-3 text-sm font-medium shadow-sm hover:opacity-95 disabled:opacity-50"
    >
      {children}
    </button>
  )
}

function SecondaryButton({ children, ...props }) {
  return (
    <button
      {...props}
      className="rounded-2xl bg-slate-100 text-slate-900 px-5 py-3 text-sm font-medium border border-slate-200 hover:bg-slate-200 disabled:opacity-50"
    >
      {children}
    </button>
  )
}

function Alert({ tone = 'neutral', children }) {
  const tones = {
    neutral: 'bg-slate-50 border-slate-200 text-slate-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    danger: 'bg-rose-50 border-rose-200 text-rose-900',
  }

  return <div className={`rounded-2xl border p-4 text-sm ${tones[tone]}`}>{children}</div>
}

function AskPage({ auth, mockApi, onNavigate }) {
  const [question, setQuestion] = useState('Can management bypass me for overtime?')
  const askState = useApiState(null)

  async function handleAsk() {
    await askState.run(() =>
      smartRequest('ask', {
        path: '/ask',
        options: {
          method: 'POST',
          body: JSON.stringify({ question }),
        },
        auth,
      }, mockApi),
    )
  }

  return (
    <CardShell
      title="Ask Stewie"
      subtitle="Search union and safety knowledge with source-backed answers."
      action={<PrimaryButton onClick={handleAsk} disabled={askState.loading || !question.trim()} data-testid="ask-btn">{askState.loading ? 'Searching...' : 'Ask Stewie'}</PrimaryButton>}
    >
      <div className="space-y-5">
        <div>
          <FieldLabel>Ask a question</FieldLabel>
          <TextArea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Example: What PPE is required for switching?"
            data-testid="question-input"
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <SecondaryButton onClick={() => setQuestion('Can management bypass me for overtime?')}>Union Example</SecondaryButton>
          <SecondaryButton onClick={() => setQuestion('What PPE is required for switching?')}>Safety Example</SecondaryButton>
          <SecondaryButton onClick={() => setQuestion('I got called into an investigation meeting. What are my rights?')}>Discipline Example</SecondaryButton>
        </div>

        {askState.error ? <Alert tone="danger">{askState.error}</Alert> : null}

        {askState.data ? (
          <div className="rounded-3xl bg-slate-50 border border-slate-200 p-5 space-y-4" data-testid="response-panel">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white px-3 py-1 border border-slate-200">Topic: {askState.data.topic}</span>
              <span className="rounded-full bg-white px-3 py-1 border border-slate-200">Source: {askState.data.routed_source}</span>
            </div>

            <ResponseSection label="Short Answer" value={askState.data.short_answer} />
            <ResponseSection label="Plain English" value={askState.data.plain_english} />

            <div>
              <div className="text-sm font-semibold text-slate-500">Source References</div>
              <div className="mt-2 space-y-2">
                {askState.data.source_references?.length ? (
                  askState.data.source_references.map((ref, idx) => (
                    <div key={`${ref.source_name}-${idx}`} className="rounded-2xl bg-white border border-slate-200 p-3 text-sm">
                      <div className="font-medium">{ref.source_name}</div>
                      <div className="text-slate-600">{ref.section_title}</div>
                      <div className="text-slate-500 text-xs mt-1">{ref.section_number || 'No section number'}</div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl bg-white border border-slate-200 p-3 text-sm text-slate-500">No references returned.</div>
                )}
              </div>
            </div>

            <ResponseSection label="Recommended Next Step" value={askState.data.recommended_next_step} />

            {askState.data.suggest_issue_report ? (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="font-medium text-amber-900">This looks like a reportable workplace issue.</div>
                  <div className="text-sm text-amber-800 mt-1">Open the reporting page and turn this into a structured report.</div>
                </div>
                <PrimaryButton onClick={() => onNavigate('report')}>Go to Report Issue</PrimaryButton>
              </div>
            ) : null}
          </div>
        ) : (
          <Alert tone="neutral">Ask a question to see the structured response format and source references.</Alert>
        )}
      </div>
    </CardShell>
  )
}

function ResponseSection({ label, value }) {
  return (
    <div>
      <div className="text-sm font-semibold text-slate-500">{label}</div>
      <p className="mt-1 text-sm text-slate-800">{value}</p>
    </div>
  )
}

function ReportIssuePage({ auth, mockApi }) {
  const reportState = useApiState(null)
  const [form, setForm] = useState({
    issue_type: 'overtime_bypass',
    location: 'North Yard',
    classification: 'Substation',
    morale_score: '3',
    description: 'I believe I was skipped for Saturday overtime callout.',
    is_anonymous: false,
  })

  async function handleSubmit() {
    await reportState.run(() =>
      smartRequest('report', {
        path: '/report',
        options: {
          method: 'POST',
          body: JSON.stringify({
            ...form,
            morale_score: form.morale_score ? Number(form.morale_score) : null,
          }),
        },
        auth,
      }, mockApi),
    )
  }

  return (
    <CardShell
      title="Report Workplace Issue"
      subtitle="Named or anonymous reporting tied to your analytics workflow."
      action={<PrimaryButton onClick={handleSubmit} disabled={reportState.loading} data-testid="submit-btn">{reportState.loading ? 'Submitting...' : 'Submit Report'}</PrimaryButton>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Issue Type</FieldLabel>
            <Select value={form.issue_type} onChange={(e) => setForm((prev) => ({ ...prev, issue_type: e.target.value }))} data-testid="issue-type-select">
              <option value="overtime_bypass">Overtime Bypass</option>
              <option value="safety_concern">Safety Concern</option>
              <option value="discipline_issue">Discipline Issue</option>
              <option value="staffing_shortage">Staffing Shortage</option>
              <option value="scheduling_issue">Scheduling Issue</option>
              <option value="contract_violation">Contract Violation</option>
              <option value="other">Other</option>
            </Select>
          </div>

          <div>
            <FieldLabel>Location</FieldLabel>
            <TextInput value={form.location} onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))} placeholder="North Yard" data-testid="location-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Classification</FieldLabel>
            <Select value={form.classification} onChange={(e) => setForm((prev) => ({ ...prev, classification: e.target.value }))} data-testid="classification-select">
              <option>Substation</option>
              <option>Lineman</option>
              <option>Operator</option>
              <option>Mechanic</option>
              <option>Other</option>
            </Select>
          </div>

          <div>
            <FieldLabel>Morale Score</FieldLabel>
            <Select value={form.morale_score} onChange={(e) => setForm((prev) => ({ ...prev, morale_score: e.target.value }))} data-testid="morale-select">
              <option value="1">1 - Very Low</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5 - Very High</option>
            </Select>
          </div>
        </div>

        <div>
          <FieldLabel>Description</FieldLabel>
          <TextArea value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Describe what happened..." data-testid="description-input" />
        </div>

        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-3">
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={form.is_anonymous}
              onChange={(e) => setForm((prev) => ({ ...prev, is_anonymous: e.target.checked }))}
              data-testid="anonymous-checkbox"
            />
            <span>
              Submit anonymously. When enabled, the report should not store identity fields in the analytics database.
            </span>
          </label>

          <div className="text-xs text-slate-500">
            Current request mode: {form.is_anonymous ? 'Anonymous' : 'Named'}
          </div>
        </div>

        {reportState.error ? <Alert tone="danger">{reportState.error}</Alert> : null}
        {reportState.data ? <Alert tone="success">Report submitted successfully. Report ID: {reportState.data.report_id}</Alert> : null}
      </div>
    </CardShell>
  )
}

function JobBriefingPage({ auth, mockApi }) {
  const briefingState = useApiState(null)
  const [form, setForm] = useState({
    work_type: 'switching',
    work_environment: 'Substation energized equipment',
  })

  async function handleGenerate() {
    await briefingState.run(() =>
      smartRequest('job-briefing', {
        path: '/job-briefing',
        options: {
          method: 'POST',
          body: JSON.stringify(form),
        },
        auth,
      }, mockApi),
    )
  }

  return (
    <CardShell
      title="Job Briefing Assistant"
      subtitle="Fast field prep for hazards, PPE, reminders, and source-backed safety prompts."
      action={<PrimaryButton onClick={handleGenerate} disabled={briefingState.loading} data-testid="generate-btn">{briefingState.loading ? 'Generating...' : 'Generate Briefing'}</PrimaryButton>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Work Type</FieldLabel>
            <Select value={form.work_type} onChange={(e) => setForm((prev) => ({ ...prev, work_type: e.target.value }))} data-testid="work-type-select">
              <option value="switching">Switching</option>
              <option value="substation maintenance">Substation Maintenance</option>
              <option value="grounding">Grounding</option>
              <option value="testing">Testing</option>
              <option value="inspection">Inspection</option>
              <option value="other">Other</option>
            </Select>
          </div>

          <div>
            <FieldLabel>Work Environment</FieldLabel>
            <TextInput value={form.work_environment} onChange={(e) => setForm((prev) => ({ ...prev, work_environment: e.target.value }))} placeholder="Substation energized equipment" data-testid="work-environment-input" />
          </div>
        </div>

        {briefingState.error ? <Alert tone="danger">{briefingState.error}</Alert> : null}

        {briefingState.data ? (
          <div className="rounded-3xl bg-slate-50 border border-slate-200 p-5 space-y-4" data-testid="briefing-panel">
            <BulletBlock label="Potential Hazards" items={briefingState.data.potential_hazards || []} />
            <BulletBlock label="Recommended PPE" items={briefingState.data.recommended_ppe || []} />
            <BulletBlock label="Safety Reminders" items={briefingState.data.safety_reminders || []} />

            <div>
              <div className="text-sm font-semibold text-slate-500">Source References</div>
              <div className="mt-2 space-y-2">
                {(briefingState.data.source_references || []).map((ref, idx) => (
                  <div key={`${ref.source_name}-${idx}`} className="rounded-2xl bg-white border border-slate-200 p-3 text-sm">
                    <div className="font-medium">{ref.source_name}</div>
                    <div className="text-slate-600">{ref.section_title}</div>
                    <div className="text-slate-500 text-xs mt-1">{ref.section_number || 'No section number'}</div>
                  </div>
                ))}
              </div>
            </div>

            <Alert tone="warning">{briefingState.data.disclaimer}</Alert>
          </div>
        ) : (
          <Alert tone="neutral">Generate a briefing to see hazards, PPE, reminders, and source references.</Alert>
        )}
      </div>
    </CardShell>
  )
}

function BulletBlock({ label, items }) {
  return (
    <div>
      <div className="text-sm font-semibold text-slate-500">{label}</div>
      <ul className="mt-2 space-y-2 text-sm text-slate-700 list-disc pl-5">
        {items.length ? items.map((item, idx) => <li key={`${label}-${idx}`}>{item}</li>) : <li>No items returned.</li>}
      </ul>
    </div>
  )
}

// Empty timeline event helper
const emptyTimelineEvent = () => ({
  time: '',
  action: '',
  equipment_involved: '',
  who_was_present: '',
  notes: '',
})

function FactFinderPage({ auth, mockApi }) {
  const factFinderState = useApiState(null)
  const [form, setForm] = useState({
    incident_description: 'Supervisor told me there will be a fact finder tomorrow about a switching error during breaker operations.',
    meeting_type: 'fact finder',
    user_role: 'involved',
    task_being_performed: 'breaker switching',
    representative_mentioned: false,
    location: 'substation',
    timeline: [
      {
        time: '08:15',
        action: 'Reviewed switching order with crew',
        equipment_involved: 'breaker 12A',
        who_was_present: 'crew leader, operator',
        notes: 'Initial briefing completed',
      },
    ],
  })

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const updateTimeline = (index, field, value) => {
    const updated = [...form.timeline]
    updated[index] = { ...updated[index], [field]: value }
    setForm((prev) => ({ ...prev, timeline: updated }))
  }

  const addTimelineEvent = () => {
    setForm((prev) => ({
      ...prev,
      timeline: [...prev.timeline, emptyTimelineEvent()],
    }))
  }

  const removeTimelineEvent = (index) => {
    setForm((prev) => ({
      ...prev,
      timeline: prev.timeline.filter((_, i) => i !== index),
    }))
  }

  async function handlePrepare() {
    const cleanedTimeline = form.timeline.filter((item) => item.action.trim() !== '')
    const payload = { ...form, timeline: cleanedTimeline }

    await factFinderState.run(() =>
      smartRequest('factFinder', {
        path: '/api/fact-finder/prepare',
        options: {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        auth,
        useAbsolutePath: true,
      }, mockApi),
    )
  }

  return (
    <CardShell
      title="Fact Finder Preparation"
      subtitle="Organize your incident details, build a timeline, and get structured preparation guidance."
      action={
        <PrimaryButton
          onClick={handlePrepare}
          disabled={factFinderState.loading || form.incident_description.length < 10}
          data-testid="prepare-btn"
        >
          {factFinderState.loading ? 'Preparing...' : 'Prepare Guidance'}
        </PrimaryButton>
      }
    >
      <div className="space-y-5">
        <div>
          <FieldLabel>Incident Description *</FieldLabel>
          <TextArea
            value={form.incident_description}
            onChange={(e) => updateField('incident_description', e.target.value)}
            placeholder="Describe what happened..."
            data-testid="incident-description"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Meeting Type</FieldLabel>
            <TextInput
              value={form.meeting_type}
              onChange={(e) => updateField('meeting_type', e.target.value)}
              placeholder="Fact finder, investigation meeting..."
              data-testid="meeting-type"
            />
          </div>

          <div>
            <FieldLabel>Your Role</FieldLabel>
            <Select
              value={form.user_role}
              onChange={(e) => updateField('user_role', e.target.value)}
              data-testid="user-role"
            >
              <option value="">Select...</option>
              <option value="involved">Directly involved</option>
              <option value="witness">Witness</option>
              <option value="supervisor">Supervisor</option>
              <option value="crew member">Crew member</option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Task Being Performed</FieldLabel>
            <TextInput
              value={form.task_being_performed}
              onChange={(e) => updateField('task_being_performed', e.target.value)}
              placeholder="Switching, breaker replacement, inspection..."
              data-testid="task-performed"
            />
          </div>

          <div>
            <FieldLabel>Location</FieldLabel>
            <TextInput
              value={form.location}
              onChange={(e) => updateField('location', e.target.value)}
              placeholder="Substation, yard, job site..."
              data-testid="location"
            />
          </div>
        </div>

        <div>
          <FieldLabel>Was a representative mentioned?</FieldLabel>
          <Select
            value={form.representative_mentioned === null ? '' : String(form.representative_mentioned)}
            onChange={(e) =>
              updateField(
                'representative_mentioned',
                e.target.value === '' ? null : e.target.value === 'true'
              )
            }
            data-testid="representative-mentioned"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </div>

        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-slate-700">Timeline Builder</div>
            <SecondaryButton onClick={addTimelineEvent} data-testid="add-timeline-event">
              + Add Event
            </SecondaryButton>
          </div>

          <div className="space-y-4">
            {form.timeline.map((event, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white border border-slate-200 p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-500">Event {index + 1}</div>
                  {form.timeline.length > 1 && (
                    <button
                      onClick={() => removeTimelineEvent(index)}
                      className="text-sm text-rose-600 hover:text-rose-700"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextInput
                    value={event.time}
                    onChange={(e) => updateTimeline(index, 'time', e.target.value)}
                    placeholder="Time (e.g., 08:15)"
                  />
                  <TextInput
                    value={event.action}
                    onChange={(e) => updateTimeline(index, 'action', e.target.value)}
                    placeholder="Action taken *"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextInput
                    value={event.equipment_involved}
                    onChange={(e) => updateTimeline(index, 'equipment_involved', e.target.value)}
                    placeholder="Equipment involved"
                  />
                  <TextInput
                    value={event.who_was_present}
                    onChange={(e) => updateTimeline(index, 'who_was_present', e.target.value)}
                    placeholder="Who was present"
                  />
                </div>

                <TextInput
                  value={event.notes}
                  onChange={(e) => updateTimeline(index, 'notes', e.target.value)}
                  placeholder="Notes"
                />
              </div>
            ))}
          </div>
        </div>

        {factFinderState.error ? <Alert tone="danger">{factFinderState.error}</Alert> : null}

        {factFinderState.data ? (
          <div className="rounded-3xl bg-slate-50 border border-slate-200 p-5 space-y-5" data-testid="fact-finder-result">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white px-3 py-1 border border-slate-200 font-medium">
                {factFinderState.data.incident_type}
              </span>
            </div>

            <div>
              <div className="text-sm font-semibold text-slate-500">Possible Topics</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {factFinderState.data.possible_topics.map((topic, idx) => (
                  <span key={idx} className="rounded-full bg-white px-3 py-1 border border-slate-200 text-sm">
                    {topic}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-slate-500">What This Meeting May Involve</div>
              <p className="mt-1 text-sm text-slate-800">{factFinderState.data.what_this_meeting_may_involve}</p>
            </div>

            <BulletBlock label="Preparation Checklist" items={factFinderState.data.prep_checklist} />
            <BulletBlock label="Suggested Questions to Ask" items={factFinderState.data.suggested_questions} />
            <BulletBlock label="Areas to Review" items={factFinderState.data.areas_to_review} />

            <div>
              <div className="text-sm font-semibold text-slate-500">Fact Summary</div>
              <pre className="mt-2 rounded-2xl bg-white border border-slate-200 p-4 text-sm text-slate-800 whitespace-pre-wrap font-sans">
                {factFinderState.data.fact_summary}
              </pre>
            </div>

            <Alert tone="warning">{factFinderState.data.disclaimer}</Alert>
          </div>
        ) : (
          <Alert tone="neutral">
            Enter your incident details and build a timeline, then click "Prepare Guidance" to receive structured preparation support.
          </Alert>
        )}
      </div>
    </CardShell>
  )
}

function AdminAnalyticsPage({ auth, mockApi }) {
  if (!auth.isAdmin) {
    return (
      <CardShell title="Admin Analytics" subtitle="This page is restricted to admin sessions.">
        <Alert tone="danger">Admin access required. Turn on admin mode in the session preview to use report tools.</Alert>
      </CardShell>
    )
  }

  return <AdminAnalyticsInner auth={auth} mockApi={mockApi} />
}

function AdminAnalyticsInner({ auth, mockApi }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const firstOfMonth = useMemo(() => `${today.slice(0, 8)}01`, [today])
  const reportState = useApiState(null)
  const [filters, setFilters] = useState({
    report_type: 'search_topics',
    start_date: firstOfMonth,
    end_date: today,
    topic: '',
    issue_type: '',
    location: '',
  })

  async function handleRunReport() {
    const params = new URLSearchParams({
      report_type: filters.report_type,
      start_date: filters.start_date,
      end_date: filters.end_date,
      format: 'json',
    })

    if (filters.topic) params.set('topic', filters.topic)
    if (filters.issue_type) params.set('issue_type', filters.issue_type)
    if (filters.location) params.set('location', filters.location)

    await reportState.run(() => 
      smartRequest('adminReport', {
        path: `/report?${params.toString()}`,
        options: { method: 'GET' },
        auth,
      }, mockApi)
    )
  }

  async function handleLastMonthSummary() {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const end = new Date(now.getFullYear(), now.getMonth(), 0)

    setFilters((prev) => ({
      ...prev,
      report_type: 'monthly_summary',
      start_date: start.toISOString().slice(0, 10),
      end_date: end.toISOString().slice(0, 10),
    }))

    const params = new URLSearchParams({
      report_type: 'monthly_summary',
      start_date: start.toISOString().slice(0, 10),
      end_date: end.toISOString().slice(0, 10),
      format: 'json',
    })

    await reportState.run(() => 
      smartRequest('adminReport', {
        path: `/report?${params.toString()}`,
        options: { method: 'GET' },
        auth,
      }, mockApi)
    )
  }

  function handleCsvDownload() {
    const params = new URLSearchParams({
      report_type: filters.report_type,
      start_date: filters.start_date,
      end_date: filters.end_date,
      format: 'csv',
    })

    if (filters.topic) params.set('topic', filters.topic)
    if (filters.issue_type) params.set('issue_type', filters.issue_type)
    if (filters.location) params.set('location', filters.location)

    const url = `${API_BASE}/report?${params.toString()}`
    window.open(url, '_blank')
  }

  const stats = useMemo(() => {
    const rows = reportState.data?.rows || []
    const total = rows.reduce((sum, row) => sum + (Number(row.count) || Number(row.search_count) || Number(row.report_count) || 0), 0)
    return {
      rowCount: reportState.data?.row_count || 0,
      total,
    }
  }, [reportState.data])

  return (
    <CardShell
      title="Admin Analytics"
      subtitle="Run on-demand reports, review totals, and export CSV files for leadership."
      action={<PrimaryButton onClick={handleRunReport} disabled={reportState.loading} data-testid="run-report-btn">{reportState.loading ? 'Running...' : 'Run Report'}</PrimaryButton>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div>
            <FieldLabel>Report Type</FieldLabel>
            <Select value={filters.report_type} onChange={(e) => setFilters((prev) => ({ ...prev, report_type: e.target.value }))} data-testid="report-type-select">
              <option value="search_topics">Top Search Topics</option>
              <option value="issue_summary">Issue Summary</option>
              <option value="location_summary">Location Summary</option>
              <option value="monthly_summary">Monthly Summary</option>
            </Select>
          </div>

          <div>
            <FieldLabel>Start Date</FieldLabel>
            <TextInput type="date" value={filters.start_date} onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))} data-testid="start-date" />
          </div>

          <div>
            <FieldLabel>End Date</FieldLabel>
            <TextInput type="date" value={filters.end_date} onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))} data-testid="end-date" />
          </div>

          <div>
            <FieldLabel>Topic Filter</FieldLabel>
            <Select value={filters.topic} onChange={(e) => setFilters((prev) => ({ ...prev, topic: e.target.value }))} data-testid="topic-filter">
              <option value="">All Topics</option>
              <option value="union">Union</option>
              <option value="safety">Safety</option>
              <option value="discipline">Discipline</option>
              <option value="general">General</option>
            </Select>
          </div>

          <div>
            <FieldLabel>Issue Type Filter</FieldLabel>
            <Select value={filters.issue_type} onChange={(e) => setFilters((prev) => ({ ...prev, issue_type: e.target.value }))} data-testid="issue-type-filter">
              <option value="">All Issue Types</option>
              <option value="overtime_bypass">Overtime Bypass</option>
              <option value="safety_concern">Safety Concern</option>
              <option value="discipline_issue">Discipline Issue</option>
              <option value="staffing_shortage">Staffing Shortage</option>
              <option value="scheduling_issue">Scheduling Issue</option>
            </Select>
          </div>

          <div>
            <FieldLabel>Location Filter</FieldLabel>
            <TextInput value={filters.location} onChange={(e) => setFilters((prev) => ({ ...prev, location: e.target.value }))} placeholder="North Yard" data-testid="location-filter" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <SecondaryButton onClick={handleCsvDownload} data-testid="download-csv-btn">Download CSV</SecondaryButton>
          <SecondaryButton onClick={handleLastMonthSummary} disabled={reportState.loading}>Last Month Executive Summary</SecondaryButton>
        </div>

        {reportState.error ? <Alert tone="danger">{reportState.error}</Alert> : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard label="Rows Returned" value={String(stats.rowCount)} />
          <MetricCard label="Aggregate Total" value={String(stats.total)} />
          <MetricCard label="Report Type" value={filters.report_type} />
        </div>

        {reportState.data ? (
          <div className="space-y-4" data-testid="report-results">
            <Alert tone="success">
              Loaded {reportState.data.row_count} rows from {reportState.data.start_date} through {reportState.data.end_date}.
            </Alert>

            <div className="overflow-hidden rounded-3xl border border-slate-200">
              <ReportTable rows={reportState.data.rows || []} />
            </div>
          </div>
        ) : (
          <Alert tone="neutral">Run a report to load rows and export CSV output.</Alert>
        )}
      </div>
    </CardShell>
  )
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-3xl font-bold mt-2 break-words">{value}</div>
    </div>
  )
}

function ReportTable({ rows }) {
  if (!rows.length) {
    return <div className="bg-white p-6 text-sm text-slate-500">No rows returned.</div>
  }

  const columns = Object.keys(rows[0])

  return (
    <table className="w-full text-sm bg-white" data-testid="report-table">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          {columns.map((column) => (
            <th key={column} className="text-left px-4 py-3 font-medium capitalize">
              {column.replace(/_/g, ' ')}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx} className="border-t border-slate-100">
            {columns.map((column) => (
              <td key={`${idx}-${column}`} className="px-4 py-3 text-slate-800">
                {String(row[column] ?? '')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
